'use client';

/**
 * GlobeLoader — a 1:1 GSAP-driven port of the original `Globe.html` loader.
 *
 * Everything (React logic, GSAP timelines, TopoJSON parsing, SVG filters and the
 * full stylesheet) lives in this single file. All CSS @keyframes and the original
 * setTimeout spotlight scheduler are replaced by GSAP:
 *   - one repeating timeline (`drift`) is the AUTHORITATIVE clock; the spotlight
 *     rides it as precomputed `.call()` callbacks, so strip <-> spotlight sync is
 *     guaranteed by construction (the original merely assumed it).
 *   - spins/ticks, the anisotropic sheen, the marker pulses and the label
 *     entrances are all GSAP, compositor-only (transform + opacity).
 *
 * SSR/static-export safe: the tick ring and the 120 capital nodes are pure
 * functions of constants (identical markup on server + first client render); the
 * only async piece — the world-atlas TopoJSON — is fetched client-side and
 * injected as children of the (already animating) `.map-strip` after hydration.
 */

import { useEffect, useId, useRef, useState } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { CustomEase } from 'gsap/CustomEase';

gsap.registerPlugin(useGSAP, CustomEase);

/* ── Eases (exact CSS equivalents) ─────────────────────────────────────────── */
const SHEEN = CustomEase.create('glSheen', 'M0,0 C0.6,0.05 0.4,0.95 1,1'); // cubic-bezier(.6,.05,.4,.95)
const CSS_IN_OUT = CustomEase.create('glInOut', 'M0,0 C0.42,0 0.58,1 1,1'); // ease-in-out
const CAP_IN = CustomEase.create('glCapIn', 'M0,0 C0.2,0.7 0.2,1 1,1'); // cubic-bezier(.2,.7,.2,1)
const CSS_EASE = CustomEase.create('glEase', 'M0,0 C0.25,0.1 0.25,1 1,1'); // ease (240ms transitions)

/* ── Spotlight / projection constants (ported verbatim) ────────────────────── */
const PERIOD = 30; // s — drift cycle
const LEAD = 0.6; // s — entrance finishes as marker hits centre (LEAD_MS 600)
const TRAIL = 2.4; // s — marker drifts before exit (TRAIL_MS 2400)
const MIN_GAP = 1.4; // s — pacing floor (MIN_GAP_MS 1400)
const LAT_MAX = 83.6; // matches topojson bbox; coupling kept verbatim — do NOT "fix"
const GLOBE_PX = 380;
const SAFE_R = (GLOBE_PX * 0.85) / 2; // inscribed safe-circle radius (~161.5px)
const STRIP_VPAD = 0.06; // 6% top + 6% bottom padding
const LBL_W = 180;
const LBL_H = 38;
const GAP = 18;

const BASE_SHADOW = '0 0 0 0 rgba(245,245,247,0.0), 0 0 6px rgba(245,245,247,0.55)';
const ACTIVE_SHADOW = '0 0 0 2px rgba(245,245,247,0.18), 0 0 14px rgba(245,245,247,0.85)';

const DEFAULT_WORLD_URL =
  'https://cdn.jsdelivr.net/npm/world-atlas@2.0.2/countries-110m.json';

/* ── Types ─────────────────────────────────────────────────────────────────── */
type Anchor = 'n' | 's' | 'e' | 'w';
type Capital = readonly [city: string, country: string, lon: number, lat: number, importance: 1 | 2];

interface CapitalGroup {
  index: number;
  lon: number;
  lat: number;
  delay: number; // first-iteration phase offset for the base pulse (s)
  a: HTMLDivElement | null;
  b: HTMLDivElement | null;
  markerA: HTMLDivElement | null;
  markerB: HTMLDivElement | null;
  labelA: HTMLDivElement | null;
  labelB: HTMLDivElement | null;
  activePulse?: gsap.core.Tween;
}

interface MapPaths {
  landD: string;
  bordersD: string;
  vb: string;
}

interface TopoTransform {
  scale: [number, number];
  translate: [number, number];
}
interface TopoGeometry {
  type: string;
  arcs?: number[][] | number[][][];
}
interface GeometryCollection {
  type: 'GeometryCollection';
  geometries: TopoGeometry[];
}
interface Topology {
  type: 'Topology';
  transform: TopoTransform;
  bbox?: [number, number, number, number];
  arcs: [number, number][][];
  objects: { countries: GeometryCollection | TopoGeometry };
}

/* ── Frozen capitals dataset (60 entries; Russia & Belarus intentionally absent) ─ */
const CAPITALS: readonly Capital[] = Object.freeze([
  ['Washington', 'United States', -77.0369, 38.9072, 1],
  ['Ottawa', 'Canada', -75.6972, 45.4215, 2],
  ['Mexico City', 'Mexico', -99.1332, 19.4326, 1],
  ['Havana', 'Cuba', -82.3666, 23.1136, 2],
  ['Guatemala City', 'Guatemala', -90.5069, 14.6349, 2],
  ['Bogotá', 'Colombia', -74.0721, 4.711, 1],
  ['Caracas', 'Venezuela', -66.9036, 10.4806, 2],
  ['Lima', 'Peru', -77.0428, -12.0464, 1],
  ['Santiago', 'Chile', -70.6693, -33.4489, 2],
  ['Buenos Aires', 'Argentina', -58.3816, -34.6037, 1],
  ['Brasília', 'Brazil', -47.9292, -15.7975, 1],
  ['Reykjavík', 'Iceland', -21.8174, 64.1466, 2],
  ['Dublin', 'Ireland', -6.2603, 53.3498, 2],
  ['Lisbon', 'Portugal', -9.1393, 38.7223, 2],
  ['London', 'United Kingdom', -0.1276, 51.5074, 1],
  ['Madrid', 'Spain', -3.7038, 40.4168, 2],
  ['Paris', 'France', 2.3522, 48.8566, 1],
  ['Brussels', 'Belgium', 4.3517, 50.8503, 2],
  ['Amsterdam', 'Netherlands', 4.9041, 52.3676, 2],
  ['Berlin', 'Germany', 13.405, 52.52, 1],
  ['Oslo', 'Norway', 10.7522, 59.9139, 2],
  ['Stockholm', 'Sweden', 18.0686, 59.3293, 2],
  ['Rome', 'Italy', 12.4964, 41.9028, 2],
  ['Vienna', 'Austria', 16.3738, 48.2082, 2],
  ['Warsaw', 'Poland', 21.0122, 52.2297, 2],
  ['Athens', 'Greece', 23.7275, 37.9838, 2],
  ['Istanbul', 'Türkiye', 28.9784, 41.0082, 1],
  ['Cairo', 'Egypt', 31.2357, 30.0444, 1],
  ['Khartoum', 'Sudan', 32.5599, 15.5007, 2],
  ['Addis Ababa', 'Ethiopia', 38.7578, 9.032, 2],
  ['Nairobi', 'Kenya', 36.8219, -1.2921, 2],
  ['Dar es Salaam', 'Tanzania', 39.2083, -6.7924, 2],
  ['Lagos', 'Nigeria', 3.3792, 6.5244, 1],
  ['Accra', 'Ghana', -0.187, 5.6037, 2],
  ['Dakar', 'Senegal', -17.4677, 14.6928, 2],
  ['Algiers', 'Algeria', 3.0588, 36.7538, 2],
  ['Rabat', 'Morocco', -6.8498, 34.0209, 2],
  ['Pretoria', 'South Africa', 28.1881, -25.7479, 1],
  ['Riyadh', 'Saudi Arabia', 46.6753, 24.7136, 1],
  ['Tehran', 'Iran', 51.389, 35.6892, 1],
  ['Baghdad', 'Iraq', 44.3661, 33.3152, 2],
  ['Jerusalem', 'Israel', 35.2137, 31.7683, 2],
  ['Doha', 'Qatar', 51.531, 25.2854, 2],
  ['Abu Dhabi', 'UAE', 54.3773, 24.4539, 2],
  ['Karachi', 'Pakistan', 67.0099, 24.8607, 1],
  ['New Delhi', 'India', 77.209, 28.6139, 1],
  ['Kathmandu', 'Nepal', 85.324, 27.7172, 2],
  ['Dhaka', 'Bangladesh', 90.4125, 23.8103, 2],
  ['Astana', 'Kazakhstan', 71.4491, 51.1694, 2],
  ['Tashkent', 'Uzbekistan', 69.2401, 41.2995, 2],
  ['Beijing', 'China', 116.4074, 39.9042, 1],
  ['Hanoi', 'Vietnam', 105.8542, 21.0285, 2],
  ['Bangkok', 'Thailand', 100.5018, 13.7563, 2],
  ['Singapore', 'Singapore', 103.8198, 1.3521, 2],
  ['Jakarta', 'Indonesia', 106.8456, -6.2088, 1],
  ['Manila', 'Philippines', 120.9842, 14.5995, 2],
  ['Seoul', 'South Korea', 126.978, 37.5665, 1],
  ['Tokyo', 'Japan', 139.6503, 35.6762, 1],
  ['Canberra', 'Australia', 149.13, -35.2809, 1],
  ['Wellington', 'New Zealand', 174.7762, -41.2865, 2],
]);

/* ── Deterministic chronograph tick ring (pure → SSR-identical) ────────────── */
const TICKS = Array.from({ length: 60 }, (_, i) => {
  const a = (i * 6) * (Math.PI / 180);
  const major = i % 5 === 0;
  const r1 = 49;
  const r2 = major ? 46 : 47.5;
  return {
    x1: (50 + Math.cos(a) * r1).toFixed(3),
    y1: (50 + Math.sin(a) * r1).toFixed(3),
    x2: (50 + Math.cos(a) * r2).toFixed(3),
    y2: (50 + Math.sin(a) * r2).toFixed(3),
    major,
  };
});

/* ── Projection + label-fit (Strategy A/B), ported verbatim ────────────────── */
function lonLatToPercent(lon: number, lat: number): [number, number] {
  const x = ((lon + 180) / 360) * 50; // 0..50% (first strip half)
  const yNorm = (LAT_MAX - lat) / (LAT_MAX * 2);
  const y = 6 + yNorm * 88; // 6..94% (matches strip padding)
  return [x, y];
}

function markerCentreXY(lat: number): [number, number] {
  const yNorm = (LAT_MAX - lat) / (LAT_MAX * 2);
  const yPct = STRIP_VPAD + yNorm * (1 - 2 * STRIP_VPAD);
  const x = 0; // relative to globe centre
  const y = (yPct - 0.5) * GLOBE_PX; // px from globe centre
  return [x, y];
}

function labelFitsAt(anchor: Anchor, mx: number, my: number): boolean {
  let lx0: number, ly0: number, lx1: number, ly1: number;
  if (anchor === 'n') {
    lx0 = mx - LBL_W / 2;
    lx1 = mx + LBL_W / 2;
    ly0 = my - GAP - LBL_H;
    ly1 = my - GAP;
  } else if (anchor === 's') {
    lx0 = mx - LBL_W / 2;
    lx1 = mx + LBL_W / 2;
    ly0 = my + GAP;
    ly1 = my + GAP + LBL_H;
  } else {
    return false;
  }
  const r2 = SAFE_R * SAFE_R;
  return (
    lx0 * lx0 + ly0 * ly0 <= r2 &&
    lx1 * lx1 + ly0 * ly0 <= r2 &&
    lx0 * lx0 + ly1 * ly1 <= r2 &&
    lx1 * lx1 + ly1 * ly1 <= r2
  );
}

function chooseAnchor(lat: number): 'n' | 's' | null {
  const primary: 'n' | 's' = lat >= 0 ? 's' : 'n';
  const secondary: 'n' | 's' = lat >= 0 ? 'n' : 's';
  const [mx, my] = markerCentreXY(lat);
  if (labelFitsAt(primary, mx, my)) return primary;
  if (labelFitsAt(secondary, mx, my)) return secondary;
  return null;
}

/* ── TopoJSON → SVG paths (delta-decode arcs, antimeridian split, dedupe borders) ─ */
function topoToPaths(topology: Topology): MapPaths {
  const obj = topology.objects.countries;
  const tx = topology.transform;
  const sx = tx.scale[0];
  const sy = tx.scale[1];
  const dx = tx.translate[0];
  const dy = tx.translate[1];
  const arcs = topology.arcs;

  // Decode delta-encoded arcs once. SVG Y is positive-down → negate lat.
  const decoded: [number, number][][] = arcs.map((arc) => {
    let x = 0;
    let y = 0;
    return arc.map((p) => {
      x += p[0];
      y += p[1];
      return [x * sx + dx, -(y * sy + dy)] as [number, number];
    });
  });

  function ptsToD(pts: [number, number][], close: boolean): string {
    let d = '';
    for (let k = 0; k < pts.length; k++) {
      d += (k === 0 ? 'M' : 'L') + pts[k][0].toFixed(2) + ',' + pts[k][1].toFixed(2);
    }
    return d + (close ? 'Z' : '');
  }

  function spansAntimeridian(pts: [number, number][]): boolean {
    for (let k = 1; k < pts.length; k++) {
      if (Math.abs(pts[k][0] - pts[k - 1][0]) > 180) return true;
    }
    return false;
  }

  function arcPts(i: number): [number, number][] {
    return i >= 0 ? decoded[i] : decoded[~i].slice().reverse();
  }

  function ringPts(arcIds: number[]): [number, number][] {
    const out: [number, number][] = [];
    for (let i = 0; i < arcIds.length; i++) {
      const pts = arcPts(arcIds[i]);
      for (let k = i === 0 ? 0 : 1; k < pts.length; k++) out.push(pts[k]);
    }
    return out;
  }

  // Split sub-paths on antimeridian wraps (Δlon > 180° projection artifacts).
  function ringToD(pts: [number, number][]): string {
    let d = '';
    let drewMove = false;
    for (let k = 0; k < pts.length; k++) {
      const [x, y] = pts[k];
      if (k === 0) {
        d += 'M' + x.toFixed(2) + ',' + y.toFixed(2);
        drewMove = true;
      } else {
        const px = pts[k - 1][0];
        if (Math.abs(x - px) > 180) {
          d += 'Z M' + x.toFixed(2) + ',' + y.toFixed(2);
        } else {
          d += 'L' + x.toFixed(2) + ',' + y.toFixed(2);
        }
      }
    }
    if (drewMove) d += 'Z';
    return d;
  }

  let landD = '';
  const usedArcs = new Set<number>();
  let bordersD = '';

  const geoms: TopoGeometry[] =
    obj.type === 'GeometryCollection' ? (obj as GeometryCollection).geometries : [obj as TopoGeometry];

  for (const g of geoms) {
    if (!g || !g.arcs) continue;
    const polys: number[][][] =
      g.type === 'MultiPolygon' ? (g.arcs as number[][][]) : [g.arcs as number[][]];
    for (const poly of polys) {
      for (const ring of poly) {
        landD += ringToD(ringPts(ring));
        for (const id of ring) {
          const a = id < 0 ? ~id : id;
          if (usedArcs.has(a)) continue;
          usedArcs.add(a);
          if (spansAntimeridian(decoded[a])) continue;
          bordersD += ptsToD(decoded[a], false);
        }
      }
    }
  }

  const bb = topology.bbox || [-180, -90, 180, 90];
  const vb = `${bb[0]} ${-bb[3]} ${bb[2] - bb[0]} ${bb[3] - bb[1]}`;
  return { landD, bordersD, vb };
}

/* ── DOM-collection helpers for a group ────────────────────────────────────── */
const markersOf = (g: CapitalGroup): HTMLDivElement[] =>
  [g.markerA, g.markerB].filter(Boolean) as HTMLDivElement[];
const labelsOf = (g: CapitalGroup): HTMLDivElement[] =>
  [g.labelA, g.labelB].filter(Boolean) as HTMLDivElement[];
const capsOf = (g: CapitalGroup): HTMLDivElement[] =>
  [g.a, g.b].filter(Boolean) as HTMLDivElement[];

function setAnchor(g: CapitalGroup, anchor: Anchor): void {
  for (const el of capsOf(g)) el.setAttribute('data-anchor', anchor);
}
function setActiveClass(g: CapitalGroup, on: boolean): void {
  for (const el of capsOf(g)) el.classList.toggle('is-active', on);
}

/* ── Component ─────────────────────────────────────────────────────────────── */
export interface GlobeLoaderProps {
  /** Override the world-atlas TopoJSON source (fetched at runtime). */
  worldCountriesUrl?: string;
  /** Extra class on the fill-parent root wrapper. */
  className?: string;
}

export default function GlobeLoader({
  worldCountriesUrl = DEFAULT_WORLD_URL,
  className,
}: GlobeLoaderProps) {
  const rootRef = useRef<HTMLDivElement>(null); // .gl-root (responsive fit)
  const containerRef = useRef<HTMLDivElement>(null); // .loader (useGSAP scope)
  const mapStripRef = useRef<HTMLDivElement>(null);
  const capitalsRef = useRef<HTMLDivElement>(null);

  const [mapData, setMapData] = useState<MapPaths | null>(null);
  const rawUid = useId();
  const uid = rawUid.replace(/:/g, ''); // per-copy SVG filter ids (client-only)

  // Build the groups once (ref callbacks populate the element slots).
  const groupsRef = useRef<CapitalGroup[] | null>(null);
  if (!groupsRef.current) {
    groupsRef.current = CAPITALS.map((c, i) => ({
      index: i,
      lon: c[2],
      lat: c[3],
      delay: ((i * 37) % 4000) / 1000,
      a: null,
      b: null,
      markerA: null,
      markerB: null,
      labelA: null,
      labelB: null,
    }));
  }

  /* ── Client-only TopoJSON fetch (abort + mounted guard) ──────────────────── */
  useEffect(() => {
    let on = true;
    const ac = new AbortController();
    fetch(worldCountriesUrl, { signal: ac.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((t: unknown) => {
        if (on && t) setMapData(topoToPaths(t as Topology));
      })
      .catch(() => {
        /* fetch failure → globe renders without continents, like the original */
      });
    return () => {
      on = false;
      ac.abort();
    };
  }, [worldCountriesUrl]);

  /* ── Responsive fit: scale the fixed 500px stage down to its container ─────
     (CSS calc can't divide length/length into a unitless scale, so measure it). */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const fit = () => {
      const { width, height } = root.getBoundingClientRect();
      const s = Math.min(1, Math.min(width, height) / 500); // 500 = --stage
      root.style.setProperty('--gl-scale', String(s || 1));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(root);
    return () => ro.disconnect();
  }, []);

  /* ── All animation, gated by reduced-motion ──────────────────────────────── */
  useGSAP(
    () => {
      const root = containerRef.current;
      const strip = mapStripRef.current;
      const caps = capitalsRef.current;
      const groups = groupsRef.current;
      if (!root || !strip || !caps || !groups) return;

      // Clear any leftover active state before (re)building a media branch.
      const resetAll = () => {
        for (const g of groups) {
          setActiveClass(g, false);
          setAnchor(g, 'n');
        }
      };

      const mm = gsap.matchMedia();

      /* ── Full motion branch ────────────────────────────────────────────── */
      mm.add('(prefers-reduced-motion: no-preference)', (self: gsap.Context) => {
        resetAll();

        let activeGroup: CapitalGroup | null = null;
        const basePulses: (gsap.core.Tween | null)[] = new Array(groups.length).fill(null);

        // Branch-scoped contextSafe handlers — created tweens revert with THIS
        // media branch (so a live reduced-motion toggle cleans them up).
        const startBasePulse = self.add('startBasePulse', (g: CapitalGroup, delay: number) => {
          const ms = markersOf(g);
          gsap.set(ms, { scale: 1, opacity: 0.35 });
          return gsap.to(ms, {
            scale: 1.25,
            opacity: 0.65,
            duration: 2,
            repeat: -1,
            yoyo: true,
            ease: CSS_IN_OUT,
            delay,
            force3D: true,
          });
        }) as (g: CapitalGroup, delay: number) => gsap.core.Tween;

        // drift = authoritative clock (both layers, one tween → frame-locked).
        const drift = gsap.timeline({ repeat: -1, defaults: { ease: 'none' } });
        drift.to([strip, caps], { xPercent: -50, duration: PERIOD, force3D: true }, 0);

        // Spins (inner rings only; .tilt keeps its static 3D tilt) + ticks.
        const t1 = root.querySelector<HTMLElement>('.t1 > .gl-ring');
        const t2 = root.querySelector<HTMLElement>('.t2 > .gl-ring');
        const t3 = root.querySelector<HTMLElement>('.t3 > .gl-ring');
        const t4 = root.querySelector<HTMLElement>('.t4 > .gl-ring');
        const ticks = root.querySelector<HTMLElement>('.ticks');
        const loops: gsap.core.Tween[] = [];
        const spin = (el: HTMLElement | null, end: number, dur: number) => {
          if (el) loops.push(gsap.to(el, { rotation: end, duration: dur, ease: 'none', repeat: -1, force3D: true }));
        };
        spin(t1, 360, 10);
        spin(t2, -360, 14);
        spin(t3, 360, 12);
        spin(t4, -360, 18);
        spin(ticks, 360, 40);

        // Sheen — two tweens (xPercent grid + opacity grid) so per-property
        // keyframe stops match the CSS exactly; rotation stays constant at -18.
        const streak = root.querySelector<HTMLElement>('.sheen-streak');
        if (streak) {
          gsap.set(streak, { rotation: -18, xPercent: -30, opacity: 0, force3D: true });
          loops.push(
            gsap.to(streak, {
              duration: 7.5,
              repeat: -1,
              keyframes: {
                '0%': { xPercent: -30 },
                '8%': { xPercent: -30 },
                '55%': { xPercent: 85 },
                '70%': { xPercent: 110 },
                '100%': { xPercent: 110 },
                easeEach: SHEEN,
              },
            }),
          );
          loops.push(
            gsap.to(streak, {
              duration: 7.5,
              repeat: -1,
              keyframes: {
                '0%': { opacity: 0 },
                '8%': { opacity: 0 },
                '14%': { opacity: 1 },
                '55%': { opacity: 1 },
                '70%': { opacity: 0 },
                '100%': { opacity: 0 },
                easeEach: SHEEN,
              },
            }),
          );
        }

        // Base marker pulse — 60 per-capital tweens (delay offsets the first
        // iteration; the infinite repeat keeps phases out of sync forever).
        for (let i = 0; i < groups.length; i++) {
          basePulses[i] = startBasePulse(groups[i], groups[i].delay);
        }

        // ── Spotlight handlers ──────────────────────────────────────────────
        const playLabelIn = (g: CapitalGroup, anchor: Anchor) => {
          const center = anchor === 'n' || anchor === 's' ? { xPercent: -50 } : { yPercent: -50 };
          const off =
            anchor === 'n' ? { y: 6 } : anchor === 's' ? { y: -6 } : anchor === 'e' ? { x: -6 } : { x: 6 };
          for (const lbl of labelsOf(g)) {
            gsap.set(lbl, { display: 'block' });
            gsap.fromTo(
              lbl,
              { autoAlpha: 0, ...center, ...off },
              { autoAlpha: 1, ...center, x: 0, y: 0, duration: 0.32, ease: CAP_IN, immediateRender: true },
            );
          }
        };

        const deactivate = self.add('deactivate', (g: CapitalGroup) => {
          if (activeGroup !== g) return;
          g.activePulse?.kill();
          g.activePulse = undefined;
          setActiveClass(g, false);
          const ms = markersOf(g);
          gsap.to(ms, {
            overwrite: true,
            scale: 1,
            opacity: 0.35,
            width: 8,
            height: 8,
            margin: '-4px 0 0 -4px',
            boxShadow: BASE_SHADOW,
            duration: 0.24,
            ease: CSS_EASE,
            onComplete: () => {
              basePulses[g.index] = startBasePulse(g, 0);
            },
          });
          for (const lbl of labelsOf(g)) gsap.set(lbl, { display: 'none', autoAlpha: 0 });
          activeGroup = null;
        }) as (g: CapitalGroup) => void;

        const activate = self.add('activate', (g: CapitalGroup, anchor: Anchor) => {
          if (activeGroup === g) return;
          if (activeGroup) deactivate(activeGroup);
          setAnchor(g, anchor);
          setActiveClass(g, true);
          const ms = markersOf(g);
          // overwrite:true kills this group's base pulse on these markers.
          gsap.to(ms, {
            overwrite: true,
            opacity: 1,
            width: 16,
            height: 16,
            margin: '-8px 0 0 -8px',
            boxShadow: ACTIVE_SHADOW,
            duration: 0.24,
            ease: CSS_EASE,
          });
          basePulses[g.index] = null;
          g.activePulse = gsap.to(ms, {
            scale: 1.15,
            duration: 0.8,
            repeat: -1,
            yoyo: true,
            ease: CSS_IN_OUT,
            force3D: true,
          });
          playLabelIn(g, anchor);
          activeGroup = g;
        }) as (g: CapitalGroup, anchor: Anchor) => void;

        // Precompute the schedule ONCE and attach to the drift playhead.
        const events = groups
          .map((g) => ({ g, cross: ((((g.lon % 360) + 360) % 360) / 360) * PERIOD }))
          .sort((p, q) => p.cross - q.cross);
        const wrap = gsap.utils.wrap(0.001, PERIOD - 0.001);
        let lastT = -Infinity;
        for (const ev of events) {
          if (ev.cross - lastT < MIN_GAP) continue; // pacing (no wrap-around check)
          const anchor = chooseAnchor(ev.g.lat);
          if (!anchor) continue; // Strategy A skip
          drift.call(activate, [ev.g, anchor], wrap(ev.cross - LEAD));
          drift.call(deactivate, [ev.g], wrap(ev.cross + TRAIL));
          lastT = ev.cross;
        }

        // Tab visibility: pause/resume freezes drift AND its callbacks together.
        const onVis = () => {
          if (document.hidden) {
            drift.pause();
            loops.forEach((t) => t.pause());
            basePulses.forEach((t) => t?.pause());
            activeGroup?.activePulse?.pause();
          } else {
            drift.resume();
            loops.forEach((t) => t.resume());
            basePulses.forEach((t) => t?.resume());
            activeGroup?.activePulse?.resume();
          }
        };
        document.addEventListener('visibilitychange', onVis);
        return () => document.removeEventListener('visibilitychange', onVis);
      });

      /* ── Reduced-motion branch (static info-graphic) ───────────────────── */
      mm.add('(prefers-reduced-motion: reduce)', () => {
        resetAll();
        gsap.set([strip, caps], { xPercent: 0 }); // freeze drift at frame 0
        const allMarkers = groups.flatMap(markersOf);
        gsap.set(allMarkers, { opacity: 0.55 });
        // Statically spotlight the first 5 fitting capitals (anchor || 's').
        for (let i = 0; i < 5 && i < groups.length; i++) {
          const g = groups[i];
          const anchor: Anchor = chooseAnchor(g.lat) || 's';
          setAnchor(g, anchor);
          setActiveClass(g, true);
          gsap.set(markersOf(g), {
            opacity: 1,
            width: 16,
            height: 16,
            margin: '-8px 0 0 -8px',
            boxShadow: ACTIVE_SHADOW,
          });
          const center = anchor === 'n' || anchor === 's' ? { xPercent: -50 } : { yPercent: -50 };
          for (const lbl of labelsOf(g)) {
            gsap.set(lbl, { display: 'block', autoAlpha: 1, ...center, x: 0, y: 0 });
          }
        }
        // sheen-streak left at its static CSS base (visible glint) — matches original.
      });

      return () => mm.revert();
    },
    { scope: containerRef, dependencies: [] },
  );

  const setCapRef =
    (i: number, half: 0 | 1) =>
    (el: HTMLDivElement | null) => {
      const g = groupsRef.current![i];
      if (half === 0) g.a = el;
      else g.b = el;
    };
  const setMarkerRef =
    (i: number, half: 0 | 1) =>
    (el: HTMLDivElement | null) => {
      const g = groupsRef.current![i];
      if (half === 0) g.markerA = el;
      else g.markerB = el;
    };
  const setLabelRef =
    (i: number, half: 0 | 1) =>
    (el: HTMLDivElement | null) => {
      const g = groupsRef.current![i];
      if (half === 0) g.labelA = el;
      else g.labelB = el;
    };

  return (
    <div ref={rootRef} className={className ? `gl-root ${className}` : 'gl-root'}>
      <div className="gl-fit">
        <div className="loader" ref={containerRef} aria-label="Loading" role="img">
          <div className="halo" aria-hidden="true" />

          <div className="rings" aria-hidden="true">
            <div className="tilt t1">
              <div className="gl-ring">
                <span className="dot" />
              </div>
            </div>
            <div className="tilt t2">
              <div className="gl-ring">
                <span className="dot small" />
              </div>
            </div>
            <div className="tilt t3">
              <div className="gl-ring">
                <span className="dot hollow" />
              </div>
            </div>
            <div className="tilt t4">
              <div className="gl-ring" />
            </div>
          </div>

          <div className="ticks" aria-hidden="true">
            <svg viewBox="0 0 100 100">
              {TICKS.map((t, i) => (
                <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2} className={t.major ? 'major' : undefined} />
              ))}
            </svg>
          </div>

          <div className="cardinal" aria-hidden="true">
            <svg viewBox="0 0 500 500" preserveAspectRatio="xMidYMid meet">
              <g>
                <line x1="250" y1="4" x2="250" y2="14" />
                <line x1="250" y1="486" x2="250" y2="496" />
                <line x1="4" y1="250" x2="14" y2="250" />
                <line x1="486" y1="250" x2="496" y2="250" />
              </g>
            </svg>
          </div>

          <div className="globe">
            <div className="map-strip" aria-hidden="true" ref={mapStripRef}>
              {mapData &&
                [0, 1].map((n) => (
                  <svg key={n} className="map" viewBox={mapData.vb} preserveAspectRatio="none">
                    <defs>
                      <filter id={`${uid}r${n}`} x="-2%" y="-2%" width="104%" height="104%">
                        <feGaussianBlur in="SourceAlpha" stdDeviation={0.5} result="blur" />
                        <feSpecularLighting
                          in="blur"
                          surfaceScale={2}
                          specularConstant={0.9}
                          specularExponent={22}
                          lightingColor="#FFFFFF"
                          result="spec"
                        >
                          <feDistantLight azimuth={315} elevation={55} />
                        </feSpecularLighting>
                        <feComposite in="spec" in2="SourceAlpha" operator="in" result="specClip" />
                        <feMerge>
                          <feMergeNode in="SourceGraphic" />
                          <feMergeNode in="specClip" />
                        </feMerge>
                      </filter>
                    </defs>
                    <path d={mapData.landD} fill="#1A1A1A" fillRule="evenodd" filter={`url(#${uid}r${n})`} />
                    <path
                      d={mapData.bordersD}
                      fill="none"
                      stroke="#F5F5F7"
                      strokeOpacity={0.9}
                      strokeWidth={0.7}
                      strokeLinejoin="round"
                      strokeLinecap="round"
                    />
                  </svg>
                ))}
            </div>

            <div className="capitals" aria-hidden="true" ref={capitalsRef}>
              {([0, 1] as const).map((half) =>
                CAPITALS.map((c, i) => {
                  const [city, country, lon, lat] = c;
                  const [x, y] = lonLatToPercent(lon, lat);
                  const left = x + half * 50;
                  const ns = lat >= 0 ? 'N' : 'S';
                  const ew = lon >= 0 ? 'E' : 'W';
                  const coords = `${Math.abs(lat).toFixed(2)}° ${ns}  ·  ${Math.abs(lon).toFixed(2)}° ${ew}`;
                  const title = `${city.toUpperCase()} · ${country.toUpperCase()}`;
                  return (
                    <div
                      key={`${half}-${i}`}
                      className="capital"
                      data-anchor="n"
                      style={{ left: `${left.toFixed(3)}%`, top: `${y.toFixed(3)}%` }}
                      ref={setCapRef(i, half)}
                    >
                      <div className="marker" ref={setMarkerRef(i, half)} />
                      <div className="label" ref={setLabelRef(i, half)}>
                        {title}
                        <span className="coords">{coords}</span>
                      </div>
                    </div>
                  );
                }),
              )}
            </div>

            <div className="gl-grid" aria-hidden="true">
              <svg viewBox="0 0 380 380" preserveAspectRatio="none">
                <ellipse cx="190" cy="190" rx="189" ry="40" />
                <ellipse cx="190" cy="190" rx="189" ry="90" />
                <ellipse cx="190" cy="190" rx="189" ry="140" />
                <ellipse cx="190" cy="190" rx="189" ry="170" />
                <line x1="1" y1="190" x2="379" y2="190" strokeWidth="0.7" />
                <line x1="190" y1="1" x2="190" y2="379" strokeWidth="0.7" />
              </svg>
            </div>

            <div className="sheen" aria-hidden="true">
              <div className="sheen-streak" />
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        /* Custom props + page backdrop live on the scoped wrapper (styled-jsx
           breaks :root/html/body). They inherit to every descendant. */
        .gl-root {
          /* Dark theme: neutral (achromatic) structural "ink" — grey for rings/ticks/graticule/labels. */
          --ink: #d6d6d6;
          --ink-soft: rgba(214, 214, 214, 0.55);
          --ink-faint: rgba(214, 214, 214, 0.22);
          --globe: 380px;
          --stage: 500px;
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
          background: transparent;
        }
        .gl-root :global(*) {
          box-sizing: border-box;
        }

        /* Scale the whole loader to fit the embed while keeping exact px geometry
           (the spotlight math hardcodes 380px). Never upscale past native size. */
        .gl-fit {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) scale(var(--gl-scale, 1));
          transform-origin: center center;
        }

        .loader {
          position: relative;
          width: var(--stage);
          height: var(--stage);
          display: grid;
          place-items: center;
          perspective: 1600px;
          transform-style: preserve-3d;
        }

        /* ── Halo ── */
        .halo {
          position: absolute;
          width: calc(var(--globe) + 80px);
          height: calc(var(--globe) + 80px);
          border-radius: 50%;
          background: radial-gradient(
            circle,
            rgba(255, 255, 255, 0) 44%,
            rgba(255, 255, 255, 0.1) 50%,
            rgba(255, 255, 255, 0.07) 56%,
            rgba(255, 255, 255, 0.045) 62%,
            rgba(255, 255, 255, 0.022) 68%,
            rgba(255, 255, 255, 0) 78%
          );
          pointer-events: none;
          z-index: 0;
        }

        /* ── Globe ── */
        .globe {
          position: relative;
          width: var(--globe);
          height: var(--globe);
          border-radius: 50%;
          overflow: hidden;
          background: radial-gradient(
            circle at 30% 28%,
            #313131 0%,
            #262626 34%,
            #1a1a1a 62%,
            #131313 86%,
            #0c0c0c 100%
          );
          box-shadow: inset 26px 28px 50px rgba(255, 255, 255, 0.05),
            inset -44px -50px 80px rgba(0, 0, 0, 0.6), inset 0 0 2px rgba(255, 255, 255, 0.14),
            0 28px 60px rgba(0, 0, 0, 0.55), 0 0 50px rgba(255, 255, 255, 0.05);
          will-change: transform;
          transform: translateZ(0);
          z-index: 2;
        }

        .map-strip {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: 200%;
          display: flex;
          will-change: transform;
          transform: translate3d(0, 0, 0);
          padding: 6% 0;
        }
        .map-strip .map {
          width: 50%;
          height: 100%;
          flex: 0 0 50%;
          display: block;
          /* Dark theme: flatten the land to luminous (white) continents instead of black. */
          filter: brightness(0) invert(1);
          opacity: 0.6;
          -webkit-mask-image: linear-gradient(
            to bottom,
            transparent 0%,
            rgba(0, 0, 0, 0.7) 4%,
            #000 10%,
            #000 90%,
            rgba(0, 0, 0, 0.7) 96%,
            transparent 100%
          );
          mask-image: linear-gradient(
            to bottom,
            transparent 0%,
            rgba(0, 0, 0, 0.7) 4%,
            #000 10%,
            #000 90%,
            rgba(0, 0, 0, 0.7) 96%,
            transparent 100%
          );
        }

        /* Graticule */
        .gl-grid {
          position: absolute;
          inset: 0;
          pointer-events: none;
          opacity: 0.3;
          z-index: 3;
        }
        .gl-grid :global(svg) {
          width: 100%;
          height: 100%;
          display: block;
        }
        .gl-grid :global(svg *) {
          fill: none;
          stroke: var(--ink);
          stroke-width: 0.5;
          vector-effect: non-scaling-stroke;
        }

        /* Top sphere shading + limb darkening */
        .globe::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background: radial-gradient(
              circle at 28% 22%,
              rgba(255, 255, 255, 0.42) 0%,
              rgba(255, 255, 255, 0.12) 14%,
              rgba(255, 255, 255, 0) 30%
            ),
            radial-gradient(ellipse 80% 40% at 50% 8%, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%),
            radial-gradient(
              circle at 78% 84%,
              rgba(0, 0, 0, 0.55) 0%,
              rgba(0, 0, 0, 0.25) 30%,
              rgba(0, 0, 0, 0) 60%
            ),
            radial-gradient(
              circle at 50% 50%,
              rgba(0, 0, 0, 0) 60%,
              rgba(0, 0, 0, 0.22) 82%,
              rgba(0, 0, 0, 0.5) 96%,
              rgba(0, 0, 0, 0.72) 100%
            );
          pointer-events: none;
          z-index: 5;
        }
        .globe::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 50%;
          box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.12), inset 0 0 0 2px rgba(255, 255, 255, 0.05);
          pointer-events: none;
          z-index: 6;
        }

        /* ── Orbital rings ── */
        .rings {
          position: absolute;
          inset: 0;
          transform-style: preserve-3d;
          pointer-events: none;
          z-index: 1;
        }
        .tilt {
          position: absolute;
          top: 50%;
          left: 50%;
          transform-style: preserve-3d;
        }
        .tilt > .gl-ring {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          will-change: transform;
          backface-visibility: hidden;
        }
        .tilt > .gl-ring .dot {
          position: absolute;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--ink);
          top: 50%;
          left: 100%;
          transform: translate(-50%, -50%);
          box-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
        }
        .tilt > .gl-ring .dot.small {
          width: 4px;
          height: 4px;
        }
        .tilt > .gl-ring .dot.hollow {
          background: transparent;
          border: 1px solid var(--ink);
          box-shadow: none;
        }

        .t1 {
          width: 480px;
          height: 480px;
          margin: -240px 0 0 -240px;
          transform: rotateX(74deg) rotateZ(20deg);
        }
        .t1 > .gl-ring {
          border: 1px solid var(--ink);
          opacity: 0.85;
        }
        .t2 {
          width: 448px;
          height: 448px;
          margin: -224px 0 0 -224px;
          transform: rotateX(22deg) rotateY(70deg) rotateZ(-24deg);
        }
        .t2 > .gl-ring {
          border: 1px dashed var(--ink);
          opacity: 0.55;
        }
        .t3 {
          width: 420px;
          height: 420px;
          margin: -210px 0 0 -210px;
          transform: rotateX(58deg) rotateY(30deg) rotateZ(45deg);
        }
        .t3 > .gl-ring {
          border: 1px dotted var(--ink);
          opacity: 0.4;
        }
        .t4 {
          width: 394px;
          height: 394px;
          margin: -197px 0 0 -197px;
          transform: rotateX(10deg) rotateY(-40deg);
        }
        .t4 > .gl-ring {
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        /* Tick-mark ring (chronograph) */
        .ticks {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 470px;
          height: 470px;
          margin: -235px 0 0 -235px;
          pointer-events: none;
          will-change: transform;
          transform-style: preserve-3d;
        }
        .ticks :global(svg) {
          width: 100%;
          height: 100%;
          display: block;
        }
        .ticks :global(svg *) {
          stroke: var(--ink);
          stroke-width: 1;
          fill: none;
          vector-effect: non-scaling-stroke;
        }
        .ticks :global(.major) {
          stroke-width: 1.4;
        }

        .cardinal {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 1;
        }
        .cardinal :global(svg) {
          width: 100%;
          height: 100%;
        }
        .cardinal :global(svg *) {
          stroke: var(--ink);
          fill: none;
          stroke-width: 1;
          vector-effect: non-scaling-stroke;
        }

        /* ── Anisotropic sheen (streak is a real element so GSAP can drive it) ── */
        .sheen {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          pointer-events: none;
          overflow: hidden;
          z-index: 4;
          -webkit-mask-image: radial-gradient(
            ellipse 70% 55% at 50% 38%,
            #000 0%,
            rgba(0, 0, 0, 0.4) 60%,
            transparent 100%
          );
          mask-image: radial-gradient(ellipse 70% 55% at 50% 38%, #000 0%, rgba(0, 0, 0, 0.4) 60%, transparent 100%);
        }
        .sheen-streak {
          position: absolute;
          top: -10%;
          left: -60%;
          width: 220%;
          height: 120%;
          background: radial-gradient(
            ellipse 14% 70% at 50% 50%,
            rgba(255, 255, 255, 0.55) 0%,
            rgba(255, 255, 255, 0.18) 35%,
            rgba(255, 255, 255, 0) 65%
          );
          transform: translateX(-30%) rotate(-18deg);
          will-change: transform, opacity;
        }

        /* ── Capitals ── */
        .capitals {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: 200%;
          pointer-events: none;
          z-index: 3;
          will-change: transform;
        }
        .capital {
          position: absolute;
          transform: translate(-50%, -50%);
          width: 0;
          height: 0;
        }
        .capital .marker {
          position: absolute;
          left: 50%;
          top: 50%;
          width: 8px;
          height: 8px;
          margin: -4px 0 0 -4px;
          border-radius: 50%;
          border: 1px solid #f5f5f7;
          background: radial-gradient(circle, #f5f5f7 0%, #f5f5f7 35%, rgba(245, 245, 247, 0) 65%);
          box-shadow: 0 0 0 0 rgba(245, 245, 247, 0), 0 0 6px rgba(245, 245, 247, 0.55);
          opacity: 0.45;
        }
        /* Crosshair hairlines — only on active (CSS opacity transition). */
        .capital .marker::before,
        .capital .marker::after {
          content: '';
          position: absolute;
          background: #f5f5f7;
          opacity: 0;
          transition: opacity 220ms ease;
          pointer-events: none;
        }
        .capital .marker::before {
          left: 50%;
          top: -10px;
          width: 1px;
          height: 26px;
          transform: translateX(-50%);
        }
        .capital .marker::after {
          top: 50%;
          left: -10px;
          width: 26px;
          height: 1px;
          transform: translateY(-50%);
        }
        .capital.is-active .marker::before,
        .capital.is-active .marker::after {
          opacity: 0.7;
        }

        /* ── Label call-out ── */
        .capital .label {
          position: absolute;
          display: none;
          white-space: nowrap;
          font-family: ui-monospace, 'SFMono-Regular', 'JetBrains Mono', Menlo, monospace;
          font-size: 10px;
          letter-spacing: 0.14em;
          color: var(--ink);
          text-transform: uppercase;
          line-height: 1.35;
          padding: 6px 10px 6px 12px;
          background: rgba(20, 20, 20, 0.82);
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 2px;
          box-shadow: 0 6px 18px rgba(26, 26, 26, 0.1);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
        }
        .capital .label .coords {
          display: block;
          font-size: 9px;
          letter-spacing: 0.08em;
          color: var(--ink-soft);
          margin-top: 2px;
          text-transform: none;
        }
        .capital .label::before {
          content: '';
          position: absolute;
          background: rgba(255, 255, 255, 0.45);
        }
        .capital[data-anchor='n'] .label {
          left: 50%;
          bottom: 18px;
          transform: translateX(-50%);
        }
        .capital[data-anchor='n'] .label::before {
          left: 50%;
          bottom: -10px;
          width: 1px;
          height: 10px;
          transform: translateX(-50%);
        }
        .capital[data-anchor='s'] .label {
          left: 50%;
          top: 18px;
          transform: translateX(-50%);
        }
        .capital[data-anchor='s'] .label::before {
          left: 50%;
          top: -10px;
          width: 1px;
          height: 10px;
          transform: translateX(-50%);
        }
        .capital[data-anchor='e'] .label {
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
        }
        .capital[data-anchor='e'] .label::before {
          left: -10px;
          top: 50%;
          width: 10px;
          height: 1px;
          transform: translateY(-50%);
        }
        .capital[data-anchor='w'] .label {
          right: 14px;
          top: 50%;
          transform: translateY(-50%);
        }
        .capital[data-anchor='w'] .label::before {
          right: -10px;
          top: 50%;
          width: 10px;
          height: 1px;
          transform: translateY(-50%);
        }
        .capital.is-active .label {
          display: block;
        }

        /* Reduced motion: static resting opacity bump (GSAP owns the rest). */
        @media (prefers-reduced-motion: reduce) {
          .capital .marker {
            opacity: 0.55;
          }
        }
      `}</style>
    </div>
  );
}
